import { useCallback, useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useContentToken,
  useSlotGroup,
  useCollectionBinding,
  usePrimaryColor,
  usePropertyAddressUrl,
  useSocialLinks,
  EditableToken,
  EditableSlotGroup,
  EditableCollectionBinding,
  RichTextContent,
} from '@vibesites/runtime/react';
import {
  FacebookIcon,
  InstagramIcon,
  FairHousingIcon,
  PetsAllowedIcon,
  WheelchairIcon,
  repliWhiteLogoUrl,
} from '@vibesites/assets';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBanSmoking } from '@fortawesome/free-solid-svg-icons/faBanSmoking';
import { VibesitesImage } from '@vibesites/ui-primitives';
import {
  getContrastTextColor,
  TEXT_ON_PRIMARY_LIGHT,
} from '../utils/luminance';
import { OPEN_PET_POLICY_MODAL_EVENT } from '../utils/petPolicyModal';
// Local black variant of the Repli wordmark — `@vibesites/assets` only ships
// `repliWhiteLogoUrl`, so we keep a dark-fill copy for light primary backgrounds.
import repliBlackLogoUrl from '../assets/repli_black.svg';
import petPolicyBgUrl from '../assets/pet-policy-bg.jpg';

// ============================================================================
// Types + helpers
// ============================================================================

interface OfficeHour {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

function hasValue(val: string | null | undefined): val is string {
  if (!val) return false;
  const normalized = val.trim().toLowerCase();
  return normalized !== '' && normalized !== 'false' && normalized !== 'null' && normalized !== 'undefined';
}

/** Formats a phone string into (XXX) XXX-XXXX. Passes through if already formatted or unrecognizable. */
function formatPhone(raw: string | null | undefined): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const d = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (d.length !== 10) return raw;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

function formatTime(raw: string): string {
  const trimmed = raw.trim();
  if (/[ap]m/i.test(trimmed)) {
    return trimmed.replace(/([ap]m)/i, (m) => m.toLowerCase());
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})$/);
  if (!match) return trimmed;

  let hours = parseInt(match[1], 10);
  const minutes = match[2];
  const period = hours >= 12 ? 'pm' : 'am';
  if (hours === 0) hours = 12;
  else if (hours > 12) hours -= 12;
  return `${hours}:${minutes} ${period}`;
}

const DAY_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const DAY_SHORT_LABELS: Record<string, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu',
  fri: 'Fri', sat: 'Sat', sun: 'Sunday',
};

interface GroupedHours {
  shortLabel: string;
  hours: string;
}

function normalizeDay(day: string): string {
  return day.trim().toLowerCase().slice(0, 3);
}

function groupOfficeHours(raw: OfficeHour[]): GroupedHours[] {
  const groups: GroupedHours[] = [];

  for (const key of DAY_ORDER) {
    const entry: OfficeHour = raw.find((e) => hasValue(e.day) && normalizeDay(e.day) === key) ?? {};
    const dayShortLabel = DAY_SHORT_LABELS[key];
    const isClosed = !hasValue(entry.open_time) || !hasValue(entry.close_time);
    const hoursStr = isClosed
      ? 'Closed'
      : `${formatTime(entry.open_time || '')} - ${formatTime(entry.close_time || '')}`;

    const prev = groups[groups.length - 1];
    if (prev && prev.hours === hoursStr) {
      const shortDash = prev.shortLabel.indexOf(' - ');
      const startShort = shortDash !== -1 ? prev.shortLabel.slice(0, shortDash) : prev.shortLabel;
      prev.shortLabel = `${startShort} - ${dayShortLabel}`;
    } else {
      groups.push({ shortLabel: dayShortLabel, hours: hoursStr });
    }
  }

  return groups;
}

/** Split a single-line address into street + city/state/zip for the two-line layout. */
function splitAddressLines(text: string): [string, string | null] {
  const trimmed = text.trim();
  if (!trimmed) return ['', null];
  // Prefer an explicit newline if the editor already formatted it that way.
  if (trimmed.includes('\n')) {
    const [first, ...rest] = trimmed.split(/\n+/);
    return [first.trim(), rest.join(', ').trim() || null];
  }
  const parts = trimmed.split(',').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    return [parts[0], parts.slice(1).join(', ')];
  }
  return [trimmed, null];
}

function NoSmokingIcon({
  size = 24,
  color = 'currentColor',
  className,
}: {
  size?: number;
  color?: string;
  className?: string;
}) {
  return (
    <FontAwesomeIcon
      icon={faBanSmoking}
      className={className}
      style={{
        width: size,
        height: size,
      }}
      color={color}
      aria-label="No Smoking"
      role="img"
    />
  );
}

// ============================================================================
// Slot Group Definitions
// ============================================================================

const logoSlotDef: SlotGroupDefinition = {
  label: 'Footer Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_mono_white_logo',
      suggestedFields: ['property_mono_white_logo', 'property_primary_logo', 'property_secondary_logo'],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Community Logo' },
  },
};

const managedByLogoDef: SlotGroupDefinition = {
  label: 'Managed By Logo',
  fields: {
    image: {
      label: 'Logo Image',
      type: 'image',
      defaultField: 'property_company_logo',
      suggestedFields: [
        'property_company_logo',
        'property_company_mono_white_logo',
        'property_company_mono_black_logo',
      ],
    },
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Property Management Company Logo' },
    url: { label: 'URL', type: 'url', defaultField: 'property_company_url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const designByLogoDef: SlotGroupDefinition = {
  label: 'Website Design By Logo',
  fields: {
    alt: { label: 'Alt Text', type: 'text', defaultValue: 'Repli Logo' },
    url: { label: 'URL', type: 'url', defaultValue: 'https://www.repli360.com/' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const addressLinkDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    text: {
      label: 'Address Text',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street, City, ST 00000',
    },
    url: { label: 'URL', type: 'url' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

const accessibilityLinkDef: SlotGroupDefinition = {
  label: 'Accessibility Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Accessibility' },
    url: { label: 'URL', type: 'url', defaultValue: '/accessibility' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const privacyPolicyLinkDef: SlotGroupDefinition = {
  label: 'Privacy Policy Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Privacy Policy' },
    url: { label: 'URL', type: 'url', defaultValue: '/privacy-policy' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const sitemapLinkDef: SlotGroupDefinition = {
  label: 'Sitemap Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Sitemap' },
    url: { label: 'URL', type: 'url', defaultValue: '/sitemap' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const termsLinkDef: SlotGroupDefinition = {
  label: 'Terms and Conditions Link',
  fields: {
    text: { label: 'Link Text', type: 'text', defaultValue: 'Terms and Conditions' },
    url: { label: 'URL', type: 'url', defaultValue: '/terms-and-conditions' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

const petPolicyBgDef: SlotGroupDefinition = {
  label: 'Pet Policy Background',
  fields: {
    image: {
      label: 'Background Image',
      type: 'image',
      defaultValue:
        'https://images.pexels.com/photos/5733432/pexels-photo-5733432.jpeg?auto=compress&cs=tinysrgb&w=1600',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Child running with a dog on a boardwalk',
    },
  },
};

const PET_POLICY_BODY_DEFAULT =
  '<p>We are a pet-friendly community! Dogs and cats are permitted - pet rent applies. There are no weight limits for dogs, but we do have breed restrictions. The following breeds are strictly prohibited: Rottweiler, Pit Bull (includes: American Pit Bull Terrier, American Staffordshire Terrier, Staffordshire Bull Terrier, American Bulldog, and Bull Terrier), Doberman, Siberian Husky, Akita, Chow, Mastiff, Great Dane, German Shepherd, Dalmatian, Alaskan Malamutes or, any mix of these breeds. For additional information or questions, please contact the leasing office.</p>';

// ============================================================================
// Component
// ============================================================================

export function Footer() {
  const phone = useContentToken('footer.phone');
  const social = useSocialLinks();
  const [petPolicyOpen, setPetPolicyOpen] = useState(false);
  const openPetPolicy = useCallback((e?: React.MouseEvent) => {
    e?.preventDefault();
    setPetPolicyOpen(true);
  }, []);
  const closePetPolicy = useCallback(() => setPetPolicyOpen(false), []);

  // Let amenity "Pet Friendly" links (and other page CTAs) open this same popup.
  useEffect(() => {
    const onOpen = () => setPetPolicyOpen(true);
    window.addEventListener(OPEN_PET_POLICY_MODAL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_PET_POLICY_MODAL_EVENT, onOpen);
  }, []);

  const primaryColor = usePrimaryColor();
  const textOnPrimary = getContrastTextColor(primaryColor);
  const useWhiteOnPrimary = textOnPrimary === TEXT_ON_PRIMARY_LIGHT;

  const runtimeLogoSlotDef: SlotGroupDefinition = useWhiteOnPrimary
    ? logoSlotDef
    : {
        ...logoSlotDef,
        fields: {
          ...logoSlotDef.fields,
          image: {
            ...logoSlotDef.fields.image,
            defaultField: 'property_mono_black_logo',
          },
        },
      };

  const logo = useSlotGroup('footer.logo', runtimeLogoSlotDef);
  const managedByLogo = useSlotGroup('footer.managed_by_logo', managedByLogoDef);
  const designByLogo = useSlotGroup('footer.design_by_logo', designByLogoDef);
  const addressLink = useSlotGroup('footer.address', addressLinkDef);
  const propertyAddressUrl = usePropertyAddressUrl();
  const accessibilityLink = useSlotGroup('footer.accessibility_link', accessibilityLinkDef);
  const privacyPolicyLink = useSlotGroup('footer.privacy_policy_link', privacyPolicyLinkDef);
  const sitemapLink = useSlotGroup('footer.sitemap_link', sitemapLinkDef);
  const termsLink = useSlotGroup('footer.terms_link', termsLinkDef);

  const officeHours = useCollectionBinding<OfficeHour>('footer.office_hours', {
    collection: 'office_hours',
  });

  const petPolicyBg = useSlotGroup('footer.pet_policy_bg', petPolicyBgDef);

  const groupedHours =
    officeHours && officeHours.length > 0 ? groupOfficeHours(officeHours) : [];

  const [streetLine, cityLine] = splitAddressLines(
    addressLink.text || '123 Main Street, City, ST 00000',
  );
  const addressHref = addressLink.url || propertyAddressUrl || '#';

  useEffect(() => {
    if (!petPolicyOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [petPolicyOpen]);

  useEffect(() => {
    if (!petPolicyOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closePetPolicy();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [petPolicyOpen, closePetPolicy]);

  // ─── RentCafe IQ chatbot z-index pin ────────────────────────────────────────
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const LAUNCHER_SELECTOR =
      '.rcc-app-cta-wrap, .rcc-app-controls, #conversations-webchat-toggle';
    const CHAT_WRAP_SELECTOR = '.rcc-app-cta-wrap';
    const Z = '120';

    function isLauncher(el: Element): boolean {
      return el.matches(LAUNCHER_SELECTOR);
    }

    function outermostFixedAncestor(el: Element): HTMLElement | null {
      let cur: Element | null = el;
      let last: HTMLElement | null = null;
      while (cur && cur !== document.body && cur instanceof HTMLElement) {
        const pos = getComputedStyle(cur).position;
        if (pos === 'fixed') last = cur;
        cur = cur.parentElement;
      }
      return last;
    }

    // Guard writes — same-value setProperty can still mutate the style
    // attribute (esp. with !important / physical+logical mix) and re-fire
    // the MutationObserver that watches document.body.
    function setImportantIfChanged(
      el: HTMLElement,
      property: string,
      value: string,
    ) {
      const style = el.style;
      if (
        style.getPropertyValue(property) !== value ||
        style.getPropertyPriority(property) !== 'important'
      ) {
        style.setProperty(property, value, 'important');
      }
    }

    function pin(el: HTMLElement) {
      setImportantIfChanged(el, 'z-index', Z);
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!isLauncher(el)) return;
      pin(el);
      const fixedAncestor = outermostFixedAncestor(el);
      if (
        fixedAncestor &&
        fixedAncestor !== el &&
        fixedAncestor.matches(CHAT_WRAP_SELECTOR)
      ) {
        pin(fixedAncestor);
      }
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(LAUNCHER_SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      if (root instanceof Element && isLauncher(root)) processCandidate(root);
    }

    scanSubtree(document.body);

    let rafId = 0;
    const observer = new MutationObserver((records) => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const seen = new Set<Element>();
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) scanSubtree(node);
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            if (seen.has(r.target)) continue;
            seen.add(r.target);
            processCandidate(r.target);
          }
        }
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class', 'style'],
    });

    return () => {
      observer.disconnect();
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  // ─── AudioEye launcher → bottom-left ────────────────────────────────────────
  // AudioEye positions `#ae_launcher` with inset-inline-* (not left/right) via
  // launcher.bundle.js. Portal config isn't controllable from theme code, so
  // force left-lower here — Footer mounts on every page via DefaultLayout.
  // Mutate productConfig when present, and re-pin inset styles whenever AE
  // remounts or rewrites the inline style attribute.
  //
  // IMPORTANT: every style write must be gated. Unconditional setProperty /
  // removeProperty on #ae_launcher feeds the body MutationObserver and
  // amplifies into a CPU-saturating loop on the deployed site.
  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined') return;

    const SELECTOR = '#ae_launcher';
    const OFFSET_PX = '24px';
    const POSITION = 'left-lower';
    const LEFT_PRESET = `ae-cta-position-preset-${POSITION}`;

    type AeWindow = Window & {
      AudioEye?: {
        productConfig?: {
          launcher?: { position?: string; [key: string]: unknown };
        };
      };
    };

    function forceProductConfig() {
      const ae = (window as AeWindow).AudioEye?.productConfig?.launcher;
      if (ae && ae.position !== POSITION) ae.position = POSITION;
    }

    function setImportantIfChanged(
      el: HTMLElement,
      property: string,
      value: string,
    ) {
      const style = el.style;
      if (
        style.getPropertyValue(property) !== value ||
        style.getPropertyPriority(property) !== 'important'
      ) {
        style.setProperty(property, value, 'important');
      }
    }

    function removeIfPresent(el: HTMLElement, property: string) {
      if (el.style.getPropertyValue(property) !== '') {
        el.style.removeProperty(property);
      }
    }

    function pinLauncher(el: HTMLElement) {
      forceProductConfig();

      // Match AudioEye's own placement API (see launcher.bundle.js `b()`).
      // Only write when the computed inline declaration differs — same-value
      // writes still mutate the style attribute when mixing physical `bottom`
      // with logical `inset-inline-*` and re-enter the observer.
      removeIfPresent(el, 'top');
      removeIfPresent(el, 'left');
      removeIfPresent(el, 'right');
      setImportantIfChanged(el, 'bottom', OFFSET_PX);
      setImportantIfChanged(el, 'inset-inline-start', OFFSET_PX);
      setImportantIfChanged(el, 'inset-inline-end', 'auto');

      const presets = Array.from(el.classList).filter((c) =>
        c.startsWith('ae-cta-position-preset-'),
      );
      for (const c of presets) {
        if (c !== LEFT_PRESET) el.classList.remove(c);
      }
      if (!el.classList.contains(LEFT_PRESET)) el.classList.add(LEFT_PRESET);
    }

    function processCandidate(el: Element) {
      if (!(el instanceof HTMLElement)) return;
      if (!el.matches(SELECTOR)) return;
      pinLauncher(el);
    }

    function scanSubtree(root: ParentNode) {
      const candidates = root.querySelectorAll(SELECTOR);
      for (const c of Array.from(candidates)) processCandidate(c);
      if (root instanceof Element && root.matches(SELECTOR)) processCandidate(root);
    }

    forceProductConfig();
    scanSubtree(document.body);

    let rafId = 0;
    const observer = new MutationObserver((records) => {
      forceProductConfig();
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        rafId = 0;
        const seen = new Set<Element>();
        let needsRescan = false;
        for (const r of records) {
          for (const node of Array.from(r.addedNodes)) {
            if (node instanceof Element) {
              scanSubtree(node);
              needsRescan = true;
            }
          }
          if (r.type === 'attributes' && r.target instanceof Element) {
            if (seen.has(r.target)) continue;
            seen.add(r.target);
            processCandidate(r.target);
          }
        }
        // Only full-body rescan when AE injects nodes; attribute-only churn
        // (our own style writes) must not re-pin the whole tree.
        if (needsRescan) scanSubtree(document.body);
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['id', 'class', 'style'],
    });

    // AE boots async; keep a short poll so we catch late productConfig + mount.
    const started = Date.now();
    const poll = window.setInterval(() => {
      forceProductConfig();
      scanSubtree(document.body);
      if (document.querySelector(SELECTOR) && Date.now() - started > 2000) {
        window.clearInterval(poll);
      } else if (Date.now() - started > 15000) {
        window.clearInterval(poll);
      }
    }, 250);

    return () => {
      observer.disconnect();
      window.clearInterval(poll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  const socialLinks = [
    social.facebook
      ? { href: social.facebook, label: 'Facebook', Icon: FacebookIcon }
      : null,
    social.instagram
      ? { href: social.instagram, label: 'Instagram', Icon: InstagramIcon }
      : null,
  ].filter(Boolean) as Array<{
    href: string;
    label: string;
    Icon: typeof FacebookIcon;
  }>;

  return (
    <footer
      className="bg-primary text-primary-foreground"
      style={{ backgroundColor: primaryColor }}
    >
      {/* ---- Main Footer Content: 4 columns ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-8 md:pt-12 md:pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8 items-start">

          {/* Column 1: Logo + social */}
          <div className="flex flex-col items-center gap-4">
            <EditableSlotGroup groupPath="footer.logo" definition={runtimeLogoSlotDef}>
              <a href="/" className="block">
                {logo.image ? (
                  <VibesitesImage
                    url={logo.image}
                    alt={logo.alt || 'Community Logo'}
                    className="h-[5.75rem] w-auto max-w-[160px] object-contain"
                    sizes="160px"
                  />
                ) : (
                  <div className="font-heading text-2xl text-primary-foreground">Our Community</div>
                )}
              </a>
            </EditableSlotGroup>

            {socialLinks.length > 0 ? (
              <div className="flex items-center gap-4" aria-label="Social media">
                {socialLinks.map(({ href, label, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="text-primary-foreground hover:opacity-70 transition-opacity"
                  >
                    <Icon size={22} color="currentColor" />
                  </a>
                ))}
              </div>
            ) : null}
          </div>

          {/* Column 2: Phone + address */}
          <div className="flex flex-col items-center gap-3 text-center">
            <a
              href={`tel:${(phone || '').replace(/[^+\d]/g, '')}`}
              className="dniphone dniphonehref font-body text-base text-primary-foreground hover:opacity-80 transition-opacity"
              style={{ fontFamily: 'var(--vibe-font-body)' }}
            >
              <EditableToken path="footer.phone">
                {formatPhone(phone)}
              </EditableToken>
            </a>

            <EditableSlotGroup groupPath="footer.address" definition={addressLinkDef}>
              <a
                href={addressHref}
                target={addressLink._target || '_blank'}
                rel="noopener noreferrer"
                className="font-body text-[15px] leading-relaxed text-primary-foreground hover:opacity-80 transition-opacity block"
              >
                <span className="block">{streetLine}</span>
                {cityLine ? <span className="block">{cityLine}</span> : null}
              </a>
            </EditableSlotGroup>
          </div>

          {/* Column 3: Office hours */}
          <div className="flex flex-col items-center">
            <EditableCollectionBinding bindingPath="footer.office_hours" label="Office Hours">
              {groupedHours.length > 0 ? (
                <dl className="space-y-2 w-full max-w-[240px]">
                  {groupedHours.map((group, index) => (
                    <div
                      key={index}
                      className="flex justify-between gap-4 font-body text-[15px] text-primary-foreground"
                    >
                      <dt>{group.shortLabel}</dt>
                      <dd className="text-right whitespace-nowrap">{group.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="font-body text-sm text-primary-foreground/40">
                  Office hours will be displayed here.
                </p>
              )}
            </EditableCollectionBinding>
          </div>

          {/* Column 4: Compliance icons + legal */}
          <div className="flex flex-col items-center gap-5">
            <div
              className="flex items-center justify-center gap-4 text-primary-foreground"
              aria-label="Community policies"
            >
              <WheelchairIcon size={26} color="currentColor" />
              <FairHousingIcon size={26} color="currentColor" />
              <button
                type="button"
                onClick={openPetPolicy}
                aria-label="Pet Policy"
                aria-haspopup="dialog"
                aria-expanded={petPolicyOpen}
                className="hover:opacity-70 transition-opacity bg-transparent border-0 p-0 cursor-pointer text-primary-foreground"
              >
                <PetsAllowedIcon size={26} color="currentColor" />
              </button>
              <NoSmokingIcon size={26} color="currentColor" />
            </div>

            <nav
              className="flex flex-wrap items-center justify-center gap-x-1 gap-y-1 font-body text-[13px] text-primary-foreground"
              aria-label="Legal links"
            >
              <EditableSlotGroup
                groupPath="footer.accessibility_link"
                definition={accessibilityLinkDef}
              >
                <a
                  href={accessibilityLink.url || '/accessibility'}
                  target={accessibilityLink._target || '_self'}
                  className="hover:opacity-70 transition-opacity"
                >
                  {accessibilityLink.text || 'Accessibility'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="text-primary-foreground/50">
                ·
              </span>
              <EditableSlotGroup
                groupPath="footer.privacy_policy_link"
                definition={privacyPolicyLinkDef}
              >
                <a
                  href={privacyPolicyLink.url || '/privacy-policy'}
                  target={privacyPolicyLink._target || '_self'}
                  className="hover:opacity-70 transition-opacity"
                >
                  {privacyPolicyLink.text || 'Privacy Policy'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="text-primary-foreground/50">
                ·
              </span>
              <EditableSlotGroup groupPath="footer.sitemap_link" definition={sitemapLinkDef}>
                <a
                  href={sitemapLink.url || '/sitemap'}
                  target={sitemapLink._target || '_self'}
                  className="hover:opacity-70 transition-opacity"
                >
                  {sitemapLink.text || 'Sitemap'}
                </a>
              </EditableSlotGroup>
              <span aria-hidden="true" className="text-primary-foreground/50">
                ·
              </span>
              <EditableSlotGroup groupPath="footer.terms_link" definition={termsLinkDef}>
                <a
                  href={termsLink.url || '/terms-and-conditions'}
                  target={termsLink._target || '_self'}
                  className="hover:opacity-70 transition-opacity"
                >
                  {termsLink.text || 'Terms and Conditions'}
                </a>
              </EditableSlotGroup>
            </nav>
          </div>
        </div>
      </div>

      {/* ---- Bottom bar: Managed By / Design By ---- */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 sm:gap-6 items-start justify-items-center">
          {/* Professionally Managed by */}
          <EditableSlotGroup
            groupPath="footer.managed_by_logo"
            definition={managedByLogoDef}
            className="flex flex-col items-center gap-2 text-center"
          >
            <RichTextContent
              path="footer.managed_by_text"
              defaultValue="<p>Professionally Managed by</p>"
              as="p"
              className="font-body text-[10px] text-primary-foreground m-0"
            />
            {managedByLogo.image ? (
              <a
                href={managedByLogo.url || '#'}
                target={managedByLogo._target || '_blank'}
                rel="noopener noreferrer"
                className="opacity-95 hover:opacity-100 transition-opacity"
              >
                <VibesitesImage
                  url={managedByLogo.image}
                  alt={managedByLogo.alt || 'Property Management Company'}
                  className="h-8 w-auto max-w-[170px] object-contain"
                  sizes="170px"
                />
              </a>
            ) : null}
          </EditableSlotGroup>

          {/* Website Design by */}
          <EditableSlotGroup
            groupPath="footer.design_by_logo"
            definition={designByLogoDef}
            className="flex flex-col items-center gap-2 text-center"
          >
            <RichTextContent
              path="footer.design_by_text"
              defaultValue="<p>Website Design by</p>"
              as="p"
              className="font-body text-[10px] text-primary-foreground m-0"
            />
            <a
              href={designByLogo.url || 'https://www.repli360.com/'}
              target={designByLogo._target || '_blank'}
              rel="noopener noreferrer"
              className="opacity-95 hover:opacity-100 transition-opacity"
            >
              <VibesitesImage
                url={useWhiteOnPrimary ? repliWhiteLogoUrl : repliBlackLogoUrl}
                alt={designByLogo.alt || 'Repli Logo'}
                height={28}
                className="h-7 w-auto"
                sizes="90px"
                priority
              />
            </a>
          </EditableSlotGroup>
        </div>
      </div>

      <style>{`
        .rcc-app-cta-wrap,
        .rcc-app-controls,
        #conversations-webchat-toggle {
          z-index: 120 !important;
        }

        /* AudioEye Accessibility Help Desk — bottom-left (overrides default right).
           AE sets placement via inset-inline-* inline styles; important beats those. */
        #ae_launcher,
        #ae_launcher.ae-cta-position-preset-right-lower,
        #ae_launcher.ae-cta-position-preset-right-mid-lower,
        #ae_launcher.ae-cta-position-preset-right-middle,
        #ae_launcher.ae-cta-position-preset-right-mid-upper,
        #ae_launcher.ae-cta-position-preset-right-upper,
        #ae_launcher.ae-cta-position-preset-left-lower {
          bottom: 24px !important;
          inset-inline-start: 24px !important;
          inset-inline-end: auto !important;
          top: auto !important;
          left: auto !important;
          right: auto !important;
        }
      `}</style>

      {petPolicyOpen ? (
        <div
          className="pet-policy-overlay"
          role="presentation"
          onClick={closePetPolicy}
        >
          <div
            className="pet-policy-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="pet-policy-title"
            onClick={(e) => e.stopPropagation()}
          >
            <EditableSlotGroup
              groupPath="footer.pet_policy_bg"
              definition={petPolicyBgDef}
              className="absolute inset-0"
            >
              <div className="absolute inset-0">
                <VibesitesImage
                  url={petPolicyBg.image || petPolicyBgUrl}
                  alt={petPolicyBg.alt || 'Child running with a dog on a boardwalk'}
                  className="h-full w-full object-cover"
                  sizes="520px"
                />
                <div className="absolute inset-0 bg-black/65" aria-hidden="true" />
              </div>
            </EditableSlotGroup>

            <div className="pet-policy-content">
              <h2 id="pet-policy-title" className="pet-policy-title">
                <RichTextContent path="footer.pet_policy_title" as="span" />
              </h2>

              <RichTextContent
                path="footer.pet_policy_body"
                defaultValue={PET_POLICY_BODY_DEFAULT}
                as="div"
                className="pet-policy-body"
              />
            </div>

            <button
              type="button"
              className="pet-policy-close"
              onClick={closePetPolicy}
              aria-label="Close pet policy"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
                <path
                  d="M2 2L12 12M12 2L2 12"
                  stroke="#1a1a1a"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>

            <div className="pet-policy-accent" aria-hidden="true" />
          </div>

          <style>{`
            .pet-policy-overlay {
              position: fixed;
              inset: 0;
              z-index: 220;
              display: flex;
              align-items: center;
              justify-content: center;
              padding: 1.25rem;
              background: rgba(0, 0, 0, 0.45);
              overflow: auto;
            }

            .pet-policy-modal {
              position: relative;
              width: 100%;
              max-width: 480px;
              aspect-ratio: 1 / 1;
              max-height: min(480px, calc(100vh - 2.5rem));
              overflow: hidden;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: none;
            }

            .pet-policy-content {
              position: relative;
              z-index: 1;
              width: 100%;
              max-height: 100%;
              overflow: auto;
              padding: 2.75rem 1.75rem 3.75rem;
              text-align: center;
              color: #ffffff;
              -ms-overflow-style: none;
              scrollbar-width: none;
            }

            .pet-policy-content::-webkit-scrollbar {
              display: none;
            }

            .pet-policy-title {
              margin: 0 0 1.15rem;
              font-family: var(--vibe-font-body);
              font-size: clamp(1.5rem, 4vw, 2rem);
              font-weight: 700;
              letter-spacing: 0.04em;
              line-height: 1.1;
              text-transform: uppercase;
              color: #ffffff;
            }

            .pet-policy-body {
              margin: 0 auto;
              font-family: var(--vibe-font-body);
              font-size: 0.9375rem;
              font-weight: 400;
              line-height: 1.55;
              color: #ffffff;
            }

            .pet-policy-body p {
              margin: 0;
            }

            .pet-policy-body a {
              color: #ffffff;
              text-decoration: underline;
            }

            .pet-policy-close {
              position: absolute;
              left: 0.85rem;
              bottom: 0.95rem;
              z-index: 2;
              display: flex;
              align-items: center;
              justify-content: center;
              width: 2.25rem;
              height: 2.25rem;
              padding: 0;
              border: none;
              border-radius: 9999px;
              background: #d4d4d4;
              cursor: pointer;
              line-height: 1;
              transition: background 0.2s ease, transform 0.2s ease;
            }

            .pet-policy-close:hover {
              background: #e5e5e5;
              transform: scale(1.04);
            }

            .pet-policy-accent {
              position: absolute;
              left: 0;
              right: 0;
              bottom: 0;
              height: 4px;
              background: var(--vibe-color-secondary, #e07a3d);
              z-index: 2;
            }

            @media (max-width: 640px) {
              .pet-policy-modal {
                max-width: 100%;
                aspect-ratio: auto;
                max-height: min(90vh, 520px);
                min-height: 360px;
              }

              .pet-policy-content {
                padding: 2.25rem 1.25rem 3.5rem;
              }
            }
          `}</style>
        </div>
      ) : null}
    </footer>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Footer',
  displayName: 'Footer',
  tokens: {
    'footer.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      defaultValue: '(555) 555-5555',
    },
    'footer.managed_by_text': {
      label: 'Managed By Text',
      type: 'richtext',
      defaultValue: '<p>Professionally Managed by</p>',
    },
    'footer.design_by_text': {
      label: 'Website Design By Text',
      type: 'richtext',
      defaultValue: '<p>Website Design by</p>',
    },
    'footer.pet_policy_title': {
      label: 'Pet Policy Title',
      type: 'richtext',
      defaultValue: '<p>PET POLICY</p>',
    },
    'footer.pet_policy_body': {
      label: 'Pet Policy Body',
      type: 'richtext',
      defaultValue:
        '<p>We are a pet-friendly community! Dogs and cats are permitted - pet rent applies. There are no weight limits for dogs, but we do have breed restrictions. The following breeds are strictly prohibited: Rottweiler, Pit Bull (includes: American Pit Bull Terrier, American Staffordshire Terrier, Staffordshire Bull Terrier, American Bulldog, and Bull Terrier), Doberman, Siberian Husky, Akita, Chow, Mastiff, Great Dane, German Shepherd, Dalmatian, Alaskan Malamutes or, any mix of these breeds. For additional information or questions, please contact the leasing office.</p>',
    },
  },
  slotGroups: {
    'footer.logo': logoSlotDef,
    'footer.managed_by_logo': managedByLogoDef,
    'footer.design_by_logo': designByLogoDef,
    'footer.address': addressLinkDef,
    'footer.accessibility_link': accessibilityLinkDef,
    'footer.privacy_policy_link': privacyPolicyLinkDef,
    'footer.sitemap_link': sitemapLinkDef,
    'footer.terms_link': termsLinkDef,
    'footer.pet_policy_bg': petPolicyBgDef,
  },
  collectionBindings: {
    'footer.office_hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(Footer as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default Footer;
